/**
 * storageErrorCopy — plain-English copy for a Firebase Storage failure.
 *
 * `FileUpload.js` was showing `err.message` straight from the SDK on a
 * failed upload — "Firebase Storage: User does not have permission to
 * access 'avatars/uid123/...webp'. (storage/unauthorized)" — vendor name,
 * object path and parenthesised code included. Same leak class as
 * `authErrorCopy.js`, for `storage/*` codes instead of `auth/*`.
 *
 * Unlike `authErrorCopy`, an unmapped error here falls back to the error's
 * OWN message, not a generic sentence: `FileUpload.js` also throws its own
 * already-friendly errors ('Crop failed', 'Image load failed') that carry
 * no `storage/*` code and must keep showing as-is.
 */

/* Codes a Storage upload/download call can return. A code with no entry
   here falls back to the error's own message — see module doc above. */
const STORAGE_ERROR_COPY = {
  'storage/unauthorized': "You don't have permission to upload here",
  'storage/unauthenticated': 'Please sign in again to upload',
  'storage/quota-exceeded': 'Storage limit reached — contact us for help',
  'storage/retry-limit-exceeded': 'Upload failed after repeated retries — check your connection and try again',
  'storage/canceled': 'Upload canceled',
  'storage/invalid-checksum': 'Upload was corrupted in transit — try again',
  'storage/server-file-wrong-size': 'Upload was corrupted in transit — try again',
  'storage/object-not-found': 'File not found — try uploading again',
  'storage/no-default-bucket': "Storage isn't configured — contact us for help",
};

/**
 * @param {*} err  the caught error (Storage error or this file's own Error)
 * @param {string} fallback  sentence shown when there is no code AND no message
 * @returns {string} one user-facing sentence, never the raw SDK message
 */
export function storageErrorCopy(err, fallback = 'Upload failed') {
  const code = String((err && err.code) || '');
  if (STORAGE_ERROR_COPY[code]) return STORAGE_ERROR_COPY[code];
  return (err && err.message) || fallback;
}
